/**
 * Wave 351 · Housy Brain capability schemas — DTOs for inspector UI + capability registry.
 */

import { z } from 'zod';
import { isoDateTimeSchema } from './common.js';
import { AutonomyLevelSchema } from './autonomy-level.js';

// ═══════════════════════════════════════════════════════════════════════════════
// Capability ID (not the same as user-facing IDs — can be symbolic)
// ═══════════════════════════════════════════════════════════════════════════════

const capabilityIdSchema = z.string().min(1).max(128);

// ═══════════════════════════════════════════════════════════════════════════════
// Enums
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Wave 351 fix · widened to match `apps/api/src/services/capability-registry.service.ts`.
 * Real values produced by `loadCapabilitiesWithRuntime()`: enabled/disabled/not_implemented
 * (see CapabilityState union). 'planned' was a planning sketch, never produced.
 */
export const CapabilityStateEnum = z.enum(['enabled', 'disabled', 'not_implemented']);
export type CapabilityState = z.infer<typeof CapabilityStateEnum>;

/**
 * Wave 351 fix · widened to match `deriveHealth()` real outputs:
 * ok / warn / down / idle / unknown. The earlier healthy/degraded/failed
 * names were never actually produced by the service.
 */
export const CapabilityHealthEnum = z.enum(['ok', 'warn', 'down', 'idle', 'unknown']);
export type CapabilityHealth = z.infer<typeof CapabilityHealthEnum>;

/**
 * Wave 351 fix · matches `apps/api/src/services/capability-registry.service.ts`
 * `CapabilityOwner = 'housy' | 'prumo' | 'shared' | 'system'`. The earlier
 * schema added an unused `external` member; the registry never produces it
 * and the AdminBrainPage tone map didn't cover it.
 */
export const CapabilityOwnerEnum = z.enum([
  'housy',
  'prumo',
  'shared',
  'system',
]);
export type CapabilityOwner = z.infer<typeof CapabilityOwnerEnum>;

// Actual tiers used in capability-registry.service
export const CapabilityTierEnum = z.enum([
  'observe',
  'propose',
  'recommend',
  'execute_with_approval',
  'execute_auto',
]);
export type CapabilityTier = z.infer<typeof CapabilityTierEnum>;

// ═══════════════════════════════════════════════════════════════════════════════
// Capability Runtime
// ═══════════════════════════════════════════════════════════════════════════════

export const CapabilityRuntimeSchema = z.object({
  state: CapabilityStateEnum,
  lastRunAt: isoDateTimeSchema.nullable(),
  // Service returns 'never' when totalRuns === 0 — keep that, plus 'unknown'
  // for D1-error fallbacks.
  lastResult: z.enum(['ok', 'failed', 'skipped', 'degraded', 'never', 'unknown']),
  lastFailureReason: z.string().nullable(),
  // Wave 351 fix · service returns null when totalRuns === 0 OR when the
  // capability has no metrics source (static_ok). The schema must accept
  // null — clamping it triggered the original FAIL.
  successRate: z.number().min(0).max(1).nullable(),
  totalRuns: z.number().int().min(0),
  health: CapabilityHealthEnum,
  preauthRuleCount: z.number().int().min(0),
  // Wave 351 fix · service exposes the *count* of enabled rules, not a
  // boolean. UI math depends on the integer; keep it numeric.
  preauthRuleEnabled: z.number().int().min(0),
});
export type CapabilityRuntime = z.infer<typeof CapabilityRuntimeSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Toggle Handle (discriminated union)
// ═══════════════════════════════════════════════════════════════════════════════

export const ToggleHandleSchema = z.union([
  z.null(),
  z.object({
    kind: z.literal('planned'),
  }),
  z.object({
    kind: z.literal('env_flag'),
    key: z.string(),
  }),
  z.object({
    kind: z.literal('autonomy_rule'),
    ruleKind: z.string(),
  }),
]);
export type ToggleHandle = z.infer<typeof ToggleHandleSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Brain Capability DTO
// ═══════════════════════════════════════════════════════════════════════════════

export const BrainCapabilityDTOSchema = z.object({
  id: capabilityIdSchema,
  cluster: z.string().describe('Capability subsystem (e.g., "proposals", "monitoring")'),
  name: z.string(),
  description: z.string(),
  owner: CapabilityOwnerEnum,
  tier: CapabilityTierEnum,
  triggers: z.array(z.string()).describe('Event names that can fire this capability'),
  outputs: z.array(z.string()).describe('Proposal kinds or action types produced'),
  sources: z.array(z.string()).optional().describe('File references for "where does this live" pane'),
  toggleHandle: ToggleHandleSchema,
  hasVerification: z.boolean().describe('Whether this capability has built-in grading/evals'),
  hasRollback: z.boolean().describe('Whether this capability can be undone'),
  defaultState: CapabilityStateEnum.describe('Default state if no override'),
  /**
   * Wave 354 · Autonomy Ladder L0–L9. Distinct from the 0–7 maturity
   * ladder that AdminBrainPage's MaturityPanel computes from runtime
   * health. Source of truth: `apps/api/src/services/capability-registry.service.ts`
   * `CapabilityDef.level`. Values are validated by the shared
   * `AutonomyLevelSchema` (0..9) and constrained against `owner` by
   * `assertOwnershipInvariant`.
   */
  level: AutonomyLevelSchema,
  runtime: CapabilityRuntimeSchema,
});
export type BrainCapabilityDTO = z.infer<typeof BrainCapabilityDTOSchema>;

export const BrainCapabilityListDTOSchema = z.object({
  items: z.array(BrainCapabilityDTOSchema),
  generatedAt: isoDateTimeSchema,
  _degraded: z.boolean().optional(),
  _reason: z.string().optional(),
});
export type BrainCapabilityListDTO = z.infer<typeof BrainCapabilityListDTOSchema>;

// ═══════════════════════════════════════════════════════════════════════════════
// Capability Rule
// ═══════════════════════════════════════════════════════════════════════════════

export const CapabilityRuleSchema = z.object({
  id: capabilityIdSchema,
  kind: z.string().describe('Rule type (e.g., "rdo_draft", "fvs_overdue")'),
  name: z.string(),
  predicate: z.unknown().describe('Rule condition as stored JSON'),
  enabled: z.coerce.boolean().describe('Stored as 0/1, coerced to boolean'),
  createdBy: capabilityIdSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type CapabilityRule = z.infer<typeof CapabilityRuleSchema>;

export const BrainCapabilityRulesDTOSchema = z.object({
  capabilityId: capabilityIdSchema,
  kind: z.string().nullable(),
  rules: z.array(CapabilityRuleSchema),
  _degraded: z.boolean().optional(),
  _reason: z.string().optional(),
});
export type BrainCapabilityRulesDTO = z.infer<typeof BrainCapabilityRulesDTOSchema>;
